import React, { useEffect, useRef } from "react";
import Footer from "./Footer";
import Navbar from "./Navbar";
import { FaShieldAlt, FaRocket, FaEye, FaBullseye, FaUsers, FaChartPie, FaAward } from "react-icons/fa";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SEO from "./SEO";

gsap.registerPlugin(ScrollTrigger);

const AboutUs: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sectionsRef = useRef<(HTMLElement | null)[]>([]);

  const companyName = import.meta.env.VITE_COMPANY_NAME || "the Company";

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power4.out" } });

      tl.from(".hero-title", {
        y: 100,
        opacity: 0,
        duration: 1.2,
        skewY: 7,
      })
      .from(".hero-subtext", {
        y: 30,
        opacity: 0,
        duration: 1,
      }, "-=0.8");

      sectionsRef.current.forEach((section) => {
        if (section) {
          gsap.from(section, {
            y: 50,
            opacity: 0,
            duration: 1,
            scrollTrigger: {
              trigger: section,
              start: "top 85%",
            }
          });
        }
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={containerRef} className="min-h-screen bg-white text-text-primary px-0 mx-0 overflow-x-hidden font-sans selection:bg-primary/10 selection:text-primary">
      <SEO
        title="About Us"
        description={`Empowering Financial Inclusion & Growth at ${companyName}. Learn about our mission and vision for a brighter financial future.`}
        keywords={`about ${companyName}, financial inclusion, NBFC, fintech, mission vision`}
      />
      
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden bg-gradient-to-b from-white via-gray-50/50 to-white" />
      
      <div className="relative z-50">
        <Navbar />
      </div>

      <main className="relative z-10">
        {/* Dynamic Storytelling Hero */}
        <section className="w-full pt-44 pb-24 flex flex-col items-center justify-center px-6 lg:px-12 relative overflow-hidden">
          <div className="max-w-7xl mx-auto flex flex-col items-center text-center">
            <div className="flex items-center gap-3 px-6 py-2 bg-white border border-secondary/20 shadow-xl rounded-full mb-10 transition-transform hover:scale-105">
              <div className="w-2 h-2 bg-secondary rounded-full animate-ping" />
              <span className="text-[10px] font-black tracking-[0.3em] text-text-secondary uppercase">The Epoch Story</span>
            </div>

            <h1 className="hero-title text-5xl sm:text-7xl md:text-8xl lg:text-9xl font-black tracking-tighter mb-10 text-text-primary leading-[0.9]">
              Driven by <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Purpose.</span>
            </h1>

            <p className="hero-subtext text-lg md:text-2xl text-text-secondary max-w-2xl leading-relaxed font-medium">
              At {companyName}, we don't just provide capital. We architect the financial bridges that turn ambitious goals into reality.
            </p>
          </div>
        </section>

        {/* Narrative & Impact */}
        <section ref={(el) => { sectionsRef.current[0] = el; }} className="w-full py-20 md:py-24 bg-white relative">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
            <div className="grid lg:grid-cols-2 gap-20 items-center">
              <div className="space-y-10">
                <h2 className="text-4xl md:text-6xl font-black tracking-tight leading-[1.1] text-text-primary">
                  Bridging the <br /><span className="text-secondary">Credit Gap.</span>
                </h2>
                <div className="space-y-6">
                  <p className="text-xl md:text-2xl font-black leading-relaxed text-text-primary decoration-secondary/30 underline-offset-8">
                    {companyName} is a RBI-Registered NBFC focused on radical financial inclusivity.
                  </p>
                  <p className="text-lg text-text-secondary font-medium leading-relaxed">
                    Founded on the principles of transparency and speed, we serve as a vital link for those underserved by traditional banking. Our team leverages decades of financial expertise to deliver tailored loan solutions that prioritize your growth over our protocols.
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-8 pt-6">
                  <div>
                    <div className="text-4xl font-black text-primary mb-2">100%</div>
                    <div className="text-xs font-black uppercase tracking-widest text-text-secondary">Digital Journey</div>
                  </div>
                  <div>
                    <div className="text-4xl font-black text-secondary mb-2">24h</div>
                    <div className="text-xs font-black uppercase tracking-widest text-text-secondary">Avg. Disbursal</div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="absolute inset-0 bg-secondary/5 rounded-[3rem] rotate-3 scale-105 -z-10" />
                <div className="bg-primary text-white p-12 md:p-16 rounded-[3rem] shadow-2xl relative overflow-hidden">
                  <div className="absolute -top-20 -right-20 w-64 h-64 bg-white/5 rounded-full blur-[80px]" />
                  <div className="flex flex-col gap-8">
                    <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center text-3xl">
                      <FaAward />
                    </div>
                    <p className="text-2xl md:text-3xl font-black leading-[1.2]">
                      "We believe every individual and business deserves a fair chance to scale their dreams."
                    </p>
                    <div className="flex items-center gap-4 text-secondary">
                      <div className="w-12 h-1 bg-secondary rounded-full" />
                      <span className="font-black text-sm uppercase tracking-widest">Our Founding Vision</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision Cards */}
        <section ref={(el) => { sectionsRef.current[1] = el; }} className="w-full py-20 md:py-24 bg-gray-50/50">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {/* Mission */}
              <div className="bg-white p-12 md:p-24 rounded-[3rem] border border-gray-100 shadow-xl transition-all duration-500 hover:shadow-2xl group overflow-hidden relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-bl-full -z-10 transition-all group-hover:scale-150" />
                <div className="flex items-center gap-6 mb-12">
                  <div className="w-16 h-16 bg-primary/10 flex items-center justify-center rounded-2xl text-primary text-3xl">
                    <FaBullseye />
                  </div>
                  <div className="text-xs font-black uppercase tracking-[0.2em] text-text-secondary">Our Mission</div>
                </div>
                <h3 className="text-4xl md:text-5xl font-black mb-8 text-text-primary tracking-tighter leading-none">
                  Accelerating <br /><span className="text-primary text-secondary">Access.</span>
                </h3>
                <p className="text-text-secondary text-xl font-medium leading-relaxed">
                  To provide frictionless, digital-first capital that empowers the backbone of the economy, ensuring no opportunity is lost due to lack of traditional credit.
                </p>
              </div>

              {/* Vision */}
              <div className="bg-white p-12 md:p-24 rounded-[3rem] border border-gray-100 shadow-xl transition-all duration-500 hover:shadow-2xl group overflow-hidden relative">
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-secondary/5 rounded-tr-full -z-10 transition-all group-hover:scale-150" />
                <div className="flex items-center gap-6 mb-12">
                  <div className="w-16 h-16 bg-secondary/10 flex items-center justify-center rounded-2xl text-secondary text-3xl">
                    <FaEye />
                  </div>
                  <div className="text-xs font-black uppercase tracking-[0.2em] text-text-secondary">Our Vision</div>
                </div>
                <h3 className="text-4xl md:text-5xl font-black mb-8 text-text-primary tracking-tighter leading-none">
                  Global Financial <br /><span className="text-secondary">Empowerment.</span>
                </h3>
                <p className="text-text-secondary text-xl font-medium leading-relaxed">
                  To be the gold standard for RBI-compliant lending, where technology and human empathy meet to create a borderless future of prosperity.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section ref={(el) => { sectionsRef.current[2] = el; }} className="w-full py-20 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
            <div className="flex flex-col items-center gap-6 mb-24">
              <div className="text-primary font-black text-[10px] uppercase tracking-[0.3em] bg-primary/5 px-6 py-2 rounded-full">Core Principles</div>
              <h2 className="text-4xl md:text-7xl font-black tracking-tight leading-none text-text-primary">
                The Pillars of <br /><span className="text-secondary tracking-tighter decoration-primary decoration-4 underline-offset-8">Epoch Trust.</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { title: "Transparency", icon: <FaShieldAlt />, desc: "Radical clarity in every term and condition." },
                { title: "Speed", icon: <FaRocket />, desc: "Instant digital processing for immediate impact." },
                { title: "Engagement", icon: <FaUsers />, desc: "Dedicated support that understands your journey." },
                { title: "Analytics", icon: <FaChartPie />, desc: "AI-driven credit logic for fairer approvals." }
              ].map((item, i) => (
                <div key={i} className="bg-white p-10 rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-500 group flex flex-col items-center">
                  <div className="w-20 h-20 bg-gray-50 rounded-2xl flex items-center justify-center text-primary text-3xl mb-8 group-hover:bg-primary group-hover:text-white transition-all duration-300 transform group-hover:-translate-y-2">
                    {item.icon}
                  </div>
                  <h4 className="text-xl font-black mb-4 text-text-primary">{item.title}</h4>
                  <p className="text-text-secondary leading-relaxed font-medium text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutUs;
